/** Pan-and-zoom maths for PhotoCropDialog. */

/** On-screen crop area, in CSS pixels. */
export const VIEW = 256;
/** Saved photo size, in pixels. */
export const OUTPUT = 512;
export const MAX_ZOOM = 4;

export type Size = { w: number; h: number };
/** `x`/`y`: where the image's top-left corner sits in the crop area, in CSS pixels. */
export type View = { zoom: number; x: number; y: number };

/** Scale at zoom 1: the image just covers the crop area. */
export function coverScale(size: Size): number {
  return VIEW / Math.min(size.w, size.h);
}

/** Keep the crop area covered: the image can't be dragged past its edges. */
export function clampView(size: Size, view: View): View {
  const zoom = Math.min(MAX_ZOOM, Math.max(1, view.zoom));
  const scale = coverScale(size) * zoom;
  const clamp = (value: number, length: number) => Math.min(0, Math.max(VIEW - length * scale, value));
  return { zoom, x: clamp(view.x, size.w), y: clamp(view.y, size.h) };
}

export function centredView(size: Size): View {
  const scale = coverScale(size);
  return { zoom: 1, x: (VIEW - size.w * scale) / 2, y: (VIEW - size.h * scale) / 2 };
}

/** Zoom keeping the image point under (px, py) in place. */
export function zoomAt(size: Size, view: View, zoom: number, px = VIEW / 2, py = VIEW / 2): View {
  const from = coverScale(size) * view.zoom;
  const to = coverScale(size) * Math.min(MAX_ZOOM, Math.max(1, zoom));
  return clampView(size, {
    zoom,
    x: px - ((px - view.x) / from) * to,
    y: py - ((py - view.y) / from) * to,
  });
}

export type Loaded = { url: string; size: Size; view: View };

/** State updater that changes the view of the loaded photo. */
export function withView(update: (size: Size, view: View) => View) {
  return (prev: Loaded | null): Loaded | null => (prev ? { ...prev, view: update(prev.size, prev.view) } : prev);
}

/** The square of the original image inside the crop area, in the image's own pixels. */
export function sourceSquare(size: Size, view: View): { x: number; y: number; side: number } {
  const scale = coverScale(size) * view.zoom;
  return { x: -view.x / scale, y: -view.y / scale, side: VIEW / scale };
}
